/**
 * Location Form Validation Schemas
 *
 * Zod validation schemas for location-related forms
 * Following the implementation plan for independent location system.
 */

import { z } from 'zod';

/**
 * Day schedule validation schema
 */
const dayScheduleSchema = z.object({
  enabled: z.boolean(),
  workingStart: z.string().optional(),
  workingEnd: z.string().optional(),
  breakStart: z.string().optional(),
  breakEnd: z.string().optional(),
});

/**
 * Opening hours validation schema
 */
const openingHoursSchema = z.object({
  monday: dayScheduleSchema.optional(),
  tuesday: dayScheduleSchema.optional(),
  wednesday: dayScheduleSchema.optional(),
  thursday: dayScheduleSchema.optional(),
  friday: dayScheduleSchema.optional(),
  saturday: dayScheduleSchema.optional(),
  sunday: dayScheduleSchema.optional(),
});

/**
 * Location form validation schema with translations
 */
export const createBranchFormSchema = (tForms: (key: string) => string) =>
  z
    .object({
      language: z.string().min(1, tForms('branch.validation.languageRequired')),
      name: z.string().min(1, tForms('branch.validation.nameRequired')),
      country: z.string().min(1, tForms('branch.validation.countryRequired')),
      street: z.string().min(1, tForms('branch.validation.streetRequired')),
      number: z.string().min(1, tForms('branch.validation.numberRequired')),
      postalCode: z.string().optional(),
      zipCode: z.string().min(1, tForms('branch.validation.zipCodeRequired')),
      city: z.string().min(1, tForms('branch.validation.cityRequired')),
      canton: z.string().optional(),
      openingHours: openingHoursSchema,
    })
    .refine(
      data => {
        // Conditional validation: Canton required for Switzerland
        if (data.country === 'CH' && !data.canton) {
          return false;
        }
        return true;
      },
      {
        message: tForms('branch.validation.cantonRequiredForSwitzerland'),
        path: ['canton'],
      }
    );

/**
 * Location contact form validation schema with translations
 * Updated to support multiple location assignment
 */
export const createBranchContactFormSchema = (
  tForms: (key: string) => string
) =>
  z.object({
    branchIds: z
      .array(z.string())
      .min(1, tForms('branchContact.validation.branchRequired')),
    language: z
      .string()
      .min(1, tForms('branchContact.validation.languageRequired')),
    function: z
      .string()
      .min(1, tForms('branchContact.validation.functionRequired')),
    isRepresentative: z.boolean(),
    address: z
      .string()
      .min(1, tForms('branchContact.validation.addressRequired')),
    firstName: z
      .string()
      .min(1, tForms('branchContact.validation.firstNameRequired')),
    lastName: z
      .string()
      .min(1, tForms('branchContact.validation.lastNameRequired')),
    email: z
      .string()
      .min(1, tForms('branchContact.validation.emailRequired'))
      .email(tForms('branchContact.validation.emailInvalid')),
    mobile: z.string().optional(),
    phone: z.string().min(1, tForms('branchContact.validation.phoneRequired')),
  });

// Type exports
export type BranchFormSchema = z.infer<
  ReturnType<typeof createBranchFormSchema>
>;
export type BranchContactFormSchema = z.infer<
  ReturnType<typeof createBranchContactFormSchema>
>;
